import { Message } from "primereact/message";
import * as React from "react";
import { ReportJobBatchEntry } from "../../../client/types.gen";
import { PerilDataSource } from "./util";
import styles from "./LayersCard.module.css";
import { useReportJobs } from "../rater/useIntegrationJobs";
import { usePrecheck, useLocalUser } from "../data";
import { catAdvisoryForSources } from "../cat/staleResults";
import { useFeatureEnabled } from "../../../IfFeatureEnabled";
import { isDefined } from "../../../util";

// Integration whose latest run produces the premium data for each peril source.
// In a perfect world this would be derived from the backend.
const INTEGRATION_BY_SOURCE = {
  air: ["ICM_YLT", "RATER"],
  rms: ["RMS_ELT", "RATER"],
  spatialkat: ["SPATIALKAT"],
  rms_22: ["RMS_22", "RATER"],
  rms_22_air_50_50: ["RMS_22", "ICM_YLT", "SPATIALKAT", "RATER"],
} as const satisfies Record<PerilDataSource, string[]>;

type SourceIntegrationType =
  (typeof INTEGRATION_BY_SOURCE)[PerilDataSource][number];

const INTEGRATION_DISPLAY_NAMES: Record<SourceIntegrationType, string> = {
  RMS_ELT: "RMS",
  RMS_22: "RMS 22",
  ICM_YLT: "ICM",
  SPATIALKAT: "KatRisk",
  RATER: "Rater",
};
const INTEGRATION_ORDER: SourceIntegrationType[] = [
  "RMS_ELT",
  "RMS_22",
  "ICM_YLT",
  "SPATIALKAT",
  "RATER",
];

const STATUS_LABELS = {
  in_progress: "in progress…",
  success: "succeeded",
  failure: "failed",
  not_started: "not yet started",
} as const;
type RunStatus = keyof typeof STATUS_LABELS;

function latestRunStatus(jobs: ReportJobBatchEntry[]): RunStatus {
  if (!jobs || jobs.length === 0) return "not_started";
  const latest = jobs.reduce((a, b) =>
    new Date(b.started_at).getTime() > new Date(a.started_at).getTime() ? b : a
  );
  return latest.outcome ?? "in_progress";
}

/**
 * Banner summarizing modeling health for the selected peril sources: the latest
 * run status per integration they depend on (in progress / failed / not yet
 * started), plus a staleness advisory when a feeding CAT vendor has a complete
 * run that the report has since drifted from. Scoped to ``sources`` so it never
 * surfaces a vendor the selected pricing scheme doesn't use.
 */
export const IntegrationStatusMessage: React.FC<{
  reportId: string;
  sources: PerilDataSource[];
}> = ({ reportId, sources }) => {
  const required = new Set(sources.flatMap((s) => INTEGRATION_BY_SOURCE[s]));
  const integrations = INTEGRATION_ORDER.filter((t) => required.has(t));

  const { jobs: allJobs, isPending } = useReportJobs(reportId);
  const { data: precheckData } = usePrecheck(reportId);
  const cat = precheckData?.cat;
  const stalenessUiEnabled = useFeatureEnabled(
    "cat-staleness-ui",
    undefined,
    useLocalUser()
  );

  // Until the first jobs fetch resolves, every integration would read as
  // "not yet started" (no jobs present) — a misleading flash on page load.
  // Render nothing until the data is in.
  if (isPending) return null;

  const statuses = integrations
    .map((type) => {
      const status = latestRunStatus(
        allJobs.filter((j) => j.integration_type === type)
      );
      if (status !== "success") {
        return { type, status };
      }
    })
    .filter(isDefined);

  // Results health is orthogonal to run status: a vendor's last run can have
  // succeeded yet have drifted since, or be modeled without backing the report's
  // results. Scoped to the CAT vendors behind the selected sources only.
  const advisory =
    stalenessUiEnabled && cat ? catAdvisoryForSources(cat, sources) : null;

  if (statuses.length === 0 && !advisory) return null;

  const severity = statuses.some((s) => s.status === "failure")
    ? "error"
    : statuses.some((s) => s.status === "not_started") ||
        advisory?.severity === "warn"
      ? "warn"
      : statuses.some((s) => s.status === "in_progress") || advisory
        ? "info"
        : "success";

  const parts: string[] = [];
  if (statuses.length > 0) {
    const runStatus = statuses
      .map(
        (s) => `${INTEGRATION_DISPLAY_NAMES[s.type]} ${STATUS_LABELS[s.status]}`
      )
      .join(" · ");
    parts.push(`Modeling: ${runStatus}`);
  }
  if (advisory) {
    parts.push(advisory.summary);
  }

  return (
    <Message
      severity={severity}
      className={styles.integrationStatusMessage}
      text={parts.join(" ")}
    />
  );
};
